import {
  addInitialPreferenceListener,
  getPreferences,
  setPreferences,
} from '@nike/ux-tread-privacy';
import { noop } from 'nav-utilities';
import { all } from './legacyV2Query.js';

const COOKIE_DURATION = 365; // 1 year in days
const SQ_EXEMPT_ATTR = 'data-sq-exempt';
/**
 * Are we currently listening for a qualifying event?
 * @type {boolean}
 */
let listeningForQualifyingEvents = false;
/**
 * Event types that can occur which imply consent.
 * i.e., if they click or focus on a input, scroll the page, etc.
 * @type {string[]}
 */
export const QUALIFYING_EVENTS = [
  'mouseup',
  'dblclick',
  'click',
  'focus',
  'blur',
  'input',
  'scroll',
  'wheel',
  'touchmove', // mobile
];

/**
 * Returns the appropriate value for the cookie domain
 * in any environment
 */
export function getDomain(hostname) {
  const hostArray = hostname.split('.');
  const index = hostArray.length === 1 ? 0 : hostArray.length - 2;
  return index === 0 ? hostArray[index] : `.${hostArray[index]}.com`;
}

/**
 * Object for interacting with the legacy SQ cookie value.
 * All of this can be safely deleted when we are able to remove the nav based cookieModal
 * and NikePrivacy is fully enabled.
 * @type {Object}
 */
export const SQCookie = {
  /**
   * Accept all cookies.
   * @returns {Object}
   */
  acceptAll() {
    return this.set({ allowMarketing: true, allowPerformance: true });
  },

  /**
   * Add callback to initial preference listerners
   * @param callback {Function}
   */
  addInitialListener(callback) {
    addInitialPreferenceListener(callback);
  },

  /**
   * Returns the permissions given the cookie value number.
   * @returns {{allowPerformance: boolean, allowMarketing: boolean}}
   */
  get() {
    return getPreferences();
  },

  /**
   * Listen for a qualifying event that will enable tracking.
   * When event is triggered, listeners are disabled.
   * @param callback {Function} optional
   * @param stopEventTrigger {Function} optional
   * @returns {Function} Removes the event listeners
   */
  listen(callback, stopEventTrigger) {
    if (listeningForQualifyingEvents) {
      return this.stopListening;
    }
    /**
     * The listener attached to each qualifying event.
     * @param event {Event}
     * @returns {Boolean}
     */
    const qualifyingEventHandler = event => {
      // Passed in check whether to trigger event
      if (stopEventTrigger && stopEventTrigger()) {
        return false;
      }

      // Just in case a rogue listener is still set. TG-1502
      if (!listeningForQualifyingEvents) {
        return false;
      }
      // If the element has the attribute, don't do anything.
      if (
        event.target.hasAttribute(SQ_EXEMPT_ATTR) ||
        event.target.parentElement.hasAttribute(SQ_EXEMPT_ATTR) ||
        event.target.hasAttribute(`${SQ_EXEMPT_ATTR}_${event.type}`) ||
        event.target.parentElement.hasAttribute(`${SQ_EXEMPT_ATTR}_${event.type}`)
      ) {
        return false;
      }
      this.acceptAll();
      // eslint-disable-next-line no-use-before-define
      stopListening();
      // istanbul ignore else
      if (callback) callback(event);
      return true;
    };
    const anchors = all('.l-header a, .pre-l-header-container a');
    /**
     * Stop listening for qualifying events.
     */
    const stopListening = () => {
      QUALIFYING_EVENTS.forEach(eventName => {
        document.body.removeEventListener(eventName, qualifyingEventHandler);
      });
      anchors.forEach(anchor => {
        anchor.removeEventListener('focus', qualifyingEventHandler);
      });
      listeningForQualifyingEvents = false;
      this.stopListening = noop;
    };
    anchors.forEach(anchor => {
      anchor.addEventListener('focus', qualifyingEventHandler);
    });

    QUALIFYING_EVENTS.forEach(eventName => {
      document.body.addEventListener(eventName, qualifyingEventHandler);
    });
    listeningForQualifyingEvents = true;
    this.stopListening = stopListening;
    return stopListening;
  },

  /**
   * Reject all cookies.
   * @returns {Object}
   */
  rejectAll() {
    return this.set({ allowMarketing: false, allowPerformance: false });
  },

  /**
   * Set the permissions to the cookie value number.
   * @param permissions {Object}
   * @returns {Object}
   */
  set(permissions) {
    const { allowPerformance, allowMarketing } = permissions;
    // Incase allowPerformance and allowMarketing are not booleans
    return setPreferences(Boolean(allowPerformance), Boolean(allowMarketing), {
      domain: getDomain(window.location.hostname),
      expires: COOKIE_DURATION,
    });
  },
  /**
   * Stop listening for implicit qualifying events.
   * @type {Function}
   */
  stopListening: noop,
};

export default SQCookie;
